import { AppCheckSdkContext, useFirebaseApp } from 'reactfire';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { useEffect, useMemo } from 'react';

import configuration from '~/configuration';
import { isBrowser } from '~/core/generic/is-browser';
import { useConsent } from '~/core/consent/ConsentProvider';

const siteKey = configuration.appCheckSiteKey;

const FirebaseAppCheckProvider: React.FCC = ({ children }) => {
  const app = useFirebaseApp();
  const { choice, ready } = useConsent();
  const decisionMade = ready && choice !== null;

  const sdk = useMemo(() => {
    // reCAPTCHA is an external security script. Wait for the visitor's first
    // explicit privacy choice before initializing it on public pages.
    if (!siteKey || !isBrowser() || !decisionMade) {
      return null;
    }

    const provider = new ReCaptchaV3Provider(siteKey);

    return initializeAppCheck(app, {
      provider,
      isTokenAutoRefreshEnabled: true,
    });
  }, [app, decisionMade]);

  useEffect(() => {
    /**
     * When running in a dev environment using a real Firebase project, we need to
     * make sure to attach the Firebase Debug Token generated from the console
     * so to disable real requests to Google Recaptcha v3
     */
    if (!configuration.production) {
      attachAppCheckDebugToken();
    }
  }, []);

  // Keep the context provider mounted while the first privacy choice enables
  // App Check, so that in-progress page state is preserved.
  return (
    <AppCheckSdkContext.Provider value={sdk ?? undefined}>
      {children}
    </AppCheckSdkContext.Provider>
  );
};

export default FirebaseAppCheckProvider;

function attachAppCheckDebugToken() {
  const token = process.env.NEXT_PUBLIC_APPCHECK_DEBUG_TOKEN;

  Object.assign(window, {
    FIREBASE_APPCHECK_DEBUG_TOKEN: token,
  });
}
